import { sessionResumeRecording } from '@/lib/analytics/scribe/session';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useChronicleStartRecording } from '@/app-shell/chronicle/hooks/useChronicleStartRecording';
import { chronicleLogger as logger } from '@/app-shell/chronicle/utils/chronicle-logger';
import {
  CHRONICLE_ORIGIN,
  chronicleSessionIdAtom,
  type ChronicleRecordingOrigin,
} from '@/atoms/chronicle';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
} from '@/lib/telemetry/desktopCaptureErrors';
import {
  finishRecordingAttempt,
  setRecordingAttemptTransport,
  type RecordingAttemptTracking,
} from '@/lib/telemetry/recordingStartTracking';
import { createSessionFlowPreflightError } from '@/lib/telemetry/sessionFlowFailures';
import { store, type Dispatch } from '@/store';
import { chronicleBridge } from '@/tauri/chronicle';
import { chronicleSessionBridge } from '@/tauri/chronicle/session';
import {
  trackHardwareRecordingResume,
  trackHardwareRecordingResumeFailed,
} from '@/utils/analytics/track-hardware-events';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';

/**
 * Core hook for resuming a Chronicle recording session.
 *
 * Handles both desktop-initiated and device-initiated resumes:
 *
 * **Desktop-initiated** (user clicks resume on desktop):
 * - `useRecordingButton` has already updated query cache with
 *   `recording_status: RECORDING` before this callback fires.
 *   Note: `useMutateUpdateSessionRecordingStatus` does NOT PATCH the
 *   backend for RECORDING status (early return) — consistent with the
 *   original desktop flow.
 * - This hook sends the BLE command to the device, resumes the timer,
 *   and tracks analytics.
 *
 * **Device-initiated** (user presses resume on Chronicle device):
 * - `ChronicleStateProvider` updates `chronicleDeviceRecordingInfoAtom`
 *   with `isPaused: false`, which `useChronicleSessionControl` detects.
 * - This hook updates the query cache (since `useRecordingButton` is NOT
 *   involved), resumes the timer, and tracks analytics. Does NOT send BLE
 *   command (device already resumed).
 *
 * **Guards**:
 * - `chronicleSessionIdAtom` must be set (active session).
 * - `isResumingRef` prevents duplicate invocations from rapid clicks or
 *   simultaneous device + desktop actions. A desktop press re-entering this
 *   instance joins the in-flight resume and takes its outcome.
 *
 * Timer: calls `timerModel.onStart(currentSeconds)` which restarts the
 * timer interval from the paused position (`timerModel.seconds` was
 * preserved by `onPause()`).
 */
/** Closes the attempt a desktop press opened; a device press carries none. */
function finishResumeAttempt(
  tracking: RecordingAttemptTracking | undefined,
  params: Parameters<typeof finishRecordingAttempt>[1] = {}
): void {
  if (tracking) finishRecordingAttempt(tracking, params);
}

export function useChronicleResumeRecording() {
  const dispatch = useDispatch<Dispatch>();
  const queryClient = useQueryClient();
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const { startChronicleRecording } = useChronicleStartRecording();
  const isResumingRef = useRef(false);
  // The attempt waiting on the in-flight resume: the newest press's.
  const pendingAttemptRef = useRef<RecordingAttemptTracking | undefined>(undefined);
  const settlePendingAttempt = useCallback(
    (params?: Parameters<typeof finishRecordingAttempt>[1]) => {
      const tracking = pendingAttemptRef.current;
      pendingAttemptRef.current = undefined;
      finishResumeAttempt(tracking, params);
    },
    []
  );

  const resumeChronicleRecording = useCallback(
    async (args: {
      origin: ChronicleRecordingOrigin;
      tracking?: RecordingAttemptTracking;
    }) => {
      setRecordingAttemptTransport(args.tracking, 'remote_device');
      if (!chronicleSessionId) {
        logger.warn(
          '[useChronicleResumeRecording] No active Chronicle session, skipping resume'
        );
        finishResumeAttempt(args.tracking, {
          outcome: 'failed',
          error: new DesktopCaptureError(
            DesktopCaptureFailure.SESSION_NOT_FOUND,
            'No active Chronicle session to resume'
          ),
        });
        return;
      }
      if (isResumingRef.current) {
        logger.warn(
          '[useChronicleResumeRecording] Already resuming, joining the in-flight resume'
        );
        // Its result closes this attempt. The one it displaces ends here; a
        // same-key opener already superseded it, so that close is a no-op.
        if (args.tracking) {
          finishResumeAttempt(pendingAttemptRef.current, {
            outcome: 'failed',
            error: createSessionFlowPreflightError(
              'StartRecordingCancelled',
              'Chronicle resume superseded by a joining press'
            ),
            metadata: { preflight_gate: 'superseded_by_retry' },
          });
          pendingAttemptRef.current = args.tracking;
        }
        return;
      }
      isResumingRef.current = true;
      pendingAttemptRef.current = args.tracking;

      const sessionId = chronicleSessionId;

      try {
        // Re-arm via start when the Rust upload session is gone — a BLE-only
        // resume would record with no upload worker and drop the segment.
        let rustIsActive = false;
        try {
          const rustState = await chronicleSessionBridge.getSessionState();
          rustIsActive = rustState.isActive;
          logger.info('[remote] resume invoked', {
            origin: args.origin,
            chronicleSessionId: sessionId,
            rustIsActive: rustState.isActive,
            rustSessionId: rustState.sessionId,
          });
        } catch (stateErr) {
          // Unreadable state → treat as inactive and re-arm: safer than a
          // BLE-only resume that could silently drop the segment.
          logger.warn('[remote] getSessionState failed; re-arming via start', {
            origin: args.origin,
            chronicleSessionId: sessionId,
            errorName: stateErr instanceof Error ? stateErr.name : typeof stateErr,
          });
        }

        if (!rustIsActive) {
          // HAR-1626: hand the live elapsed to the re-arm. A remote-first
          // recording has no Rust online session to find, so every resume takes
          // this branch — and `startChronicleRecording` would seed the timer
          // from the backend `duration`, still 0 until bulk sync. `onPause`
          // leaves `seconds` intact, and the recovery listener seeded it from
          // the device's own `MCU&RT` duration, so this is the true elapsed.
          const preservedSeconds = store.getState().timerModel.seconds;
          try {
            // `startChronicleRecording` RESOLVES on paths that started nothing
            // (an in-flight start, or another session already owning the
            // recorder). Reading the outcome is what stops a resume the device
            // never acted on from reporting a success.
            const outcome = await startChronicleRecording({
              origin: args.origin,
              targetSessionId: sessionId,
              resumeFromSeconds: preservedSeconds,
              tracking: args.tracking,
            });
            // A non-`started` outcome drove nothing. The start closes the
            // attempt it was given itself, unless it coalesced; these settles
            // cover that case and a press that joined meanwhile.
            if (outcome === 'started') {
              settlePendingAttempt();
            } else if (outcome === 'no_session') {
              settlePendingAttempt({
                outcome: 'failed',
                error: new DesktopCaptureError(
                  DesktopCaptureFailure.SESSION_NOT_FOUND,
                  'No Chronicle session to re-arm'
                ),
              });
            } else if (outcome === 'already_active') {
              // Start closed its own handle by the adoption; a joiner inherits it.
              const adopted = await chronicleSessionBridge
                .getSessionState()
                .then((s) => s.sessionId !== null)
                .catch(() => false);
              settlePendingAttempt(
                adopted
                  ? undefined
                  : {
                      outcome: 'failed',
                      error: new DesktopCaptureError(
                        DesktopCaptureFailure.SESSION_NOT_FOUND,
                        'Chronicle adoption found no active session'
                      ),
                    }
              );
            } else {
              settlePendingAttempt({
                outcome: 'failed',
                error: createSessionFlowPreflightError(
                  'StartRecordingCancelled',
                  'Chronicle resume coalesced into a start in flight'
                ),
                metadata: { preflight_gate: 'chronicle_start_in_flight' },
              });
            }
          } catch (error) {
            // startChronicleRecording already toasts; swallow to keep resume non-throwing.
            settlePendingAttempt({ outcome: 'failed', error });
            logger.error('[useChronicleResumeRecording] re-arm via start failed', {
              errorName: error instanceof Error ? error.name : typeof error,
            });
          }
          return;
        }

        // 1. Desktop-initiated: send BLE command to resume device.
        //    Device-initiated: device already resumed — skip bridge call.
        if (args.origin === CHRONICLE_ORIGIN.DESKTOP) {
          try {
            await chronicleBridge.resumeRecording();
            // Device acked: capture resumed.
            settlePendingAttempt();
          } catch (error) {
            // The device did not resume, though the UI continues. Settle first,
            // so a throwing logger cannot turn this failure into a success.
            settlePendingAttempt({ outcome: 'failed', error });
            logger.error(
              '[useChronicleResumeRecording] chronicleBridge.resumeRecording failed',
              { errorName: error instanceof Error ? error.name : typeof error }
            );
            trackHardwareRecordingResumeFailed({
              sessionIdLocal: sessionId,
              origin: args.origin,
              errorType: error instanceof Error ? error.name : 'UNKNOWN',
              // Mirrored into Datadog logs; bridge text can echo device content.
              errorDescription: 'chronicleBridge.resumeRecording failed',
            });
            // Continue — UI state should still reflect recording
          }
        }
        // A desktop press that joined a device-side resume: the device resumed.
        settlePendingAttempt();

        // 2. Device-initiated: update query cache to RECORDING.
        //    Desktop-initiated: already handled by useRecordingButton → skip.
        //    Note: no backend PATCH for RECORDING — consistent with
        //    useMutateUpdateSessionRecordingStatus which returns early.
        if (args.origin === CHRONICLE_ORIGIN.DEVICE) {
          queryClient.setQueryData<QuerySessionResponse>(
            [GET_SESSION_BY_ID, { sessionId }],
            (old) => {
              if (!old) return undefined;
              return { ...old, recording_status: RecordingStatus.RECORDING };
            }
          );
        }

        // 3. Resume timer from current seconds (preserved by onPause)
        const currentSeconds = store.getState().timerModel.seconds;
        dispatch.timerModel.onStart(currentSeconds);

        // 4. Track analytics
        sessionResumeRecording({
          sessionId,
        });
        trackHardwareRecordingResume({
          sessionId,
          sessionIdLocal: sessionId,
          origin: args.origin,
        });

        logger.debug('[useChronicleResumeRecording] Recording resumed', {
          sessionId,
          origin: args.origin,
        });
      } finally {
        isResumingRef.current = false;
        // No attempt outlives the resume it joined; a leftover never got a result.
        settlePendingAttempt({
          outcome: 'failed',
          error: createSessionFlowPreflightError(
            'StartRecordingFailed',
            'Chronicle resume ended without a result'
          ),
        });
      }
    },
    [
      chronicleSessionId,
      dispatch,
      queryClient,
      settlePendingAttempt,
      startChronicleRecording,
    ]
  );

  return { resumeChronicleRecording };
}
